import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

/**
 * A panel body that scrolls when its content outgrows the space, with the
 * house scroll affordances: the slim tinted scrollbar the repertoire settings
 * popup uses, and a chevron at the foot that appears only while there is more
 * below.
 *
 * The scrollbar is the browser's own, styled — not an overlaid thumb. An
 * overlay sits on top of the content and needs its own drag handling; a real
 * scrollbar takes up layout, so `paddingRight` keeps it clear of the rows
 * beside it, and it scrolls by every means the platform already supports.
 */

/** Thumb colour, matching the repertoire settings popup. */
const SCROLLBAR_THUMB = 'rgba(121, 216, 255, 0.45)';
/** Track behind it. */
const SCROLLBAR_TRACK = 'rgba(255,255,255,0.06)';
/** Gutter reserved so the bar never sits over the content. */
const SCROLLBAR_GUTTER_PX = 6;

/**
 * Fills the height its parent leaves it, rather than taking a height of its
 * own: the panel is sized against the header and the footer, and the body is
 * simply whatever is left after the heading. The parent must be a flex column
 * with a bounded height.
 */
const ScrollablePanelBody = ({ children }: { children: React.ReactNode }) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [showScrollMoreHint, setShowScrollMoreHint] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const { scrollHeight, clientHeight, scrollTop } = el;
    // Sub-pixel layout leaves a fractional difference on content that in fact
    // fits, which would otherwise show the hint on a panel that cannot scroll.
    const epsilon = 2;
    const overflow = scrollHeight > clientHeight + epsilon;
    const atBottom = scrollTop + clientHeight >= scrollHeight - epsilon;
    setShowScrollMoreHint(overflow && !atBottom);
  }, []);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    const inner = contentRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    // Both boxes: the viewport changes with the window, and the content changes
    // when a disclosure inside it opens.
    const observer = new ResizeObserver(() => updateScrollState());
    observer.observe(el);
    if (inner) observer.observe(inner);
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
      <Box
        ref={bodyRef}
        sx={{
          flex: 1,
          // Without this a flex child refuses to shrink below its content, so
          // the body would grow the panel instead of scrolling inside it.
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingRight: `${SCROLLBAR_GUTTER_PX}px`,
          scrollbarWidth: 'thin',
          scrollbarColor: `${SCROLLBAR_THUMB} ${SCROLLBAR_TRACK}`,
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': {
            backgroundColor: SCROLLBAR_TRACK,
            borderRadius: '999px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: SCROLLBAR_THUMB,
            borderRadius: '999px',
          },
        }}
      >
        <Box ref={contentRef}>{children}</Box>
      </Box>

      {showScrollMoreHint && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: '5px',
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
            lineHeight: 0,
          }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 22, color: 'rgba(255, 255, 255, 0.52)' }} />
        </Box>
      )}
    </Box>
  );
};

export default ScrollablePanelBody;
